/*
 * Does this visitor already have a session on the platform?
 *
 * The landing sits on silveribis.com and the app on app.silveribis.com — different
 * ORIGINS, so the app's cookie is unreadable here, but the SAME SITE (one registrable
 * domain), so the browser still sends that cookie on a request to the app. The app
 * exposes GET /session-status, which answers a bare {"logged":true|false} and echoes
 * CORS headers for this origin only.
 *
 * Purely cosmetic: it decides whether the header says "Log in" or "Go to the platform".
 * Both link to the same place and the app resolves the destination itself, so every
 * failure path here simply leaves the button reading "Log in" — never a blocked render,
 * never a wrong destination. The request is therefore fire-and-forget and every error is
 * swallowed on purpose.
 */
import { reactive } from 'vue';

export const APP_URL = 'https://app.silveribis.com';

/* "Entrar" used to drop people on the platform's home screen with the sign-in
   panel closed, so they had to find and press Acceder before they could type
   anything. The platform opens its sign-in drawer when it sees ?login=1, so the
   link carries it. Already-signed-in visitors go to the plain app URL. */
export const LOGIN_URL = APP_URL + '/?login=1';

export const session = reactive({
    logged: false,
    checked: false,
});

export function checkSession() {
    /* Abort rather than let a slow or hung app hold a request open indefinitely; the
       label is not worth it. */
    let controller = null;
    let timer = null;

    try {
        controller = new AbortController();
        timer = window.setTimeout(() => controller.abort(), 6000);
    } catch (e) {
        /* very old browser: go without the abort */
    }

    return fetch(`${APP_URL}/session-status`, {
        method: 'GET',
        credentials: 'include',   // same-site, so the platform cookie rides along
        signal: controller ? controller.signal : undefined,
        /* deliberately no custom headers: that keeps this a "simple" request and skips
           the preflight round trip */
    })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
            session.logged = !!(data && data.logged);
        })
        .catch(() => {
            /* offline, blocked by an extension, CORS refused, aborted — all mean the
               same thing here: show "Log in" */
            session.logged = false;
        })
        .finally(() => {
            if (timer) { window.clearTimeout(timer); }
            session.checked = true;
        });
}

export default {
    install(app) {
        app.config.globalProperties.$session = session;
        app.config.globalProperties.$appUrl = APP_URL;
        app.config.globalProperties.$loginUrl = LOGIN_URL;

        checkSession();
    },
};
